import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { CalendarDays, MapPin, Users, Plus, Loader2, X, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Meetings() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => api.get("/meetings").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <Layout
      title="Mesyuarat / Meetings"
      subtitle="Survey kedatangan (RSVP) & daftar kehadiran / RSVP survey & attendance"
      actions={<button onClick={() => setShowAdd(true)} data-testid="add-meeting-btn" className="flex items-center gap-1.5 bg-brass text-ink font-semibold hover:bg-brass-hover px-3 py-2 rounded-sm text-sm transition-colors"><Plus size={16} /> <span className="hidden sm:inline">Cipta / Create</span></button>}
    >
      {!data ? (
        <div className="flex items-center gap-2 text-slate"><Loader2 className="animate-spin" size={18} />Loading…</div>
      ) : data.meetings.length === 0 ? (
        <div className="bg-white border border-hairline rounded-sm py-16 text-center text-slate">Tiada mesyuarat lagi. / No meetings yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {data.meetings.map((m) => {
            const rc = m.summary.responses;
            return (
              <button key={m.id} onClick={() => nav(`/meetings/${m.id}`)} data-testid={`meeting-card-${m.id}`}
                className="text-left bg-white border border-hairline rounded-sm p-6 hover:border-brass transition-colors group">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-wider bg-ink text-paper mb-2">{m.meeting_type}</span>
                    <h3 className="font-serif text-xl text-ink">{m.title}</h3>
                  </div>
                  <ArrowRight size={18} className="text-slate/40 group-hover:text-brass transition-colors" />
                </div>
                <div className="mt-3 space-y-1.5 text-sm text-slate">
                  <div className="flex items-center gap-2"><CalendarDays size={15} className="text-brass" /> {m.date} · {m.start_time}–{m.end_time}</div>
                  <div className="flex items-center gap-2"><MapPin size={15} className="text-brass" /> {m.venue} · {m.modes?.join(" / ")}</div>
                  <div className="flex items-center gap-2"><Users size={15} className="text-brass" /> Kuorum {m.quorum} · {m.summary.checked_in} hadir</div>
                </div>
                <div className="mt-4 flex gap-2 text-xs">
                  <span className="bg-positive-bg text-positive px-2 py-1 rounded-sm">{rc.attending} Hadir</span>
                  <span className="bg-brass/10 text-brass px-2 py-1 rounded-sm">{rc.tentative} Mungkin</span>
                  <span className="bg-brick-bg text-brick px-2 py-1 rounded-sm">{rc.not_attending} Tak Hadir</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {showAdd && <AddMeetingModal onClose={() => setShowAdd(false)} onSaved={(id) => { setShowAdd(false); nav(`/meetings/${id}`); }} />}
    </Layout>
  );
}

function AddMeetingModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", meeting_type: "AGM", date: "", start_time: "10:00", end_time: "12:00", venue: "", quorum: 40 });
  const [modes, setModes] = useState({ Physical: true, Online: true });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.title.trim() || !form.date) return toast.error("Title and date are required");
    setSaving(true);
    try {
      const selModes = Object.entries(modes).filter(([, v]) => v).map(([k]) => k);
      const r = await api.post("/meetings", { ...form, quorum: parseInt(form.quorum) || 0, modes: selModes });
      toast.success("Meeting created");
      onSaved(r.data.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const field = (k, label, type = "text") => (
    <div><label className="block text-xs font-medium text-slate mb-1">{label}</label>
      <input type={type} value={form[k]} onChange={set(k)} data-testid={`meeting-${k}`} className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm focus:border-brass focus:ring-1 focus:ring-brass outline-none text-ink" /></div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-lg animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline"><h3 className="font-serif text-xl text-ink">Cipta Mesyuarat / Create Meeting</h3><button onClick={onClose} className="text-slate hover:text-ink"><X size={20} /></button></div>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">{field("title", "Tajuk / Title *")}</div>
          <div><label className="block text-xs font-medium text-slate mb-1">Jenis / Type</label>
            <select value={form.meeting_type} onChange={set("meeting_type")} data-testid="meeting-type" className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm text-ink outline-none focus:border-brass">
              <option value="AGM">AGM</option><option value="EGM">EGM</option><option value="Committee">Committee</option>
            </select></div>
          {field("date", "Tarikh / Date *", "date")}
          {field("start_time", "Mula / Start", "time")}
          {field("end_time", "Tamat / End", "time")}
          <div className="md:col-span-2">{field("venue", "Tempat / Venue")}</div>
          {field("quorum", "Kuorum / Quorum", "number")}
          <div><label className="block text-xs font-medium text-slate mb-1">Mod / Attendance Mode</label>
            <div className="flex gap-3 mt-2">
              {["Physical", "Online"].map((mo) => (
                <label key={mo} className="flex items-center gap-1.5 text-sm text-ink cursor-pointer">
                  <input type="checkbox" checked={modes[mo]} onChange={(e) => setModes({ ...modes, [mo]: e.target.checked })} data-testid={`mode-${mo}`} className="accent-brass" /> {mo}
                </label>
              ))}
            </div></div>
        </div>
        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-3">
          <button onClick={onClose} className="bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-4 py-2 rounded-sm text-sm">Batal</button>
          <button onClick={save} disabled={saving} data-testid="meeting-save" className="bg-ink text-white hover:bg-ink-light px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-60">{saving && <Loader2 size={15} className="animate-spin" />}Simpan / Save</button>
        </div>
      </div>
    </div>
  );
}
